import { useEffect, useRef, useState } from "react";
import { AlertTriangle, PhoneCall, ChevronRight, Clock, Sunrise, CalendarClock, MessageSquare, Check, X } from "lucide-react";
import api, { errorMessage } from "@/lib/api";
import { toast } from "sonner";
import { useCallTracker } from "@/components/app/CallTracker";

const REASON_CHIPS = [
  "Customer in meeting",
  "Asked for evening",
  "Line busy",
  "Not picking",
  "Sent WhatsApp",
];

/**
 * Red banner listing every lead whose LATEST Call Back follow-up is past due.
 * Row actions (one-tap):
 *   • Snooze 30m / 1h / Tomorrow 10am  → open inline reason prompt then execute
 *   • Reschedule                       → inline datetime editor
 *   • WhatsApp                         → wa.me pre-filled greeting
 */
export default function OverdueFollowupBanner({ onOpenProfile }) {
  const { finishedTick } = useCallTracker();
  const [rows, setRows] = useState([]);
  const [collapsed, setCollapsed] = useState(false);

  // Per-row editor state (only one open at a time)
  const [pendingSnooze, setPendingSnooze] = useState(null); // {row, mode, label, ...}
  const [reasonText, setReasonText] = useState("");
  const [rescheduleFor, setRescheduleFor] = useState(null);
  const [rescheduleAt, setRescheduleAt] = useState("");
  const [busyId, setBusyId] = useState(null);

  const load = () =>
    api.get("/staff/call-logs/overdue-followups")
      .then(r => setRows(r.data.overdue || []))
      .catch(() => setRows([]));

  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [finishedTick]);

  if (rows.length === 0) return null;

  const overdueLabel = (iso) => {
    const s = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
    if (s < 3600) return `${Math.floor(s / 60)}m late`;
    if (s < 86400) return `${Math.floor(s / 3600)}h late`;
    return `${Math.floor(s / 86400)}d late`;
  };
  const fmtDT = (iso) => { try { return new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); } catch { return iso; } };

  const tomorrowTenIso = () => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    d.setHours(10, 0, 0, 0);
    return d.toISOString();
  };

  const closeAllEditors = () => {
    setPendingSnooze(null); setReasonText("");
    setRescheduleFor(null); setRescheduleAt("");
  };

  const openSnooze = (row, mode) => {
    closeAllEditors();
    setPendingSnooze({ row, mode });
    setReasonText("");
  };

  const confirmSnooze = async () => {
    if (!pendingSnooze) return;
    const { row, mode } = pendingSnooze;
    setBusyId(row.id);
    try {
      const body = { reason: reasonText.trim() };
      if (mode === "30m") body.minutes = 30;
      else if (mode === "1h") body.minutes = 60;
      else if (mode === "tomorrow") body.next_followup = tomorrowTenIso();
      const r = await api.post(`/staff/call-logs/${row.id}/snooze`, body);
      toast.success(`Snoozed → ${fmtDT(r.data.next_followup)}`);
      closeAllEditors();
      load();
    } catch (e) { toast.error(errorMessage(e)); }
    finally { setBusyId(null); }
  };

  const openReschedule = (row) => {
    closeAllEditors();
    const d = new Date(Date.now() + 60 * 60 * 1000);
    d.setSeconds(0, 0);
    d.setMinutes(Math.ceil(d.getMinutes() / 5) * 5);
    const pad = (n) => String(n).padStart(2, "0");
    const local = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    setRescheduleAt(local);
    setRescheduleFor(row.id);
  };

  const saveReschedule = async (row) => {
    if (!rescheduleAt) { toast.error("Pick a date & time"); return; }
    if (new Date(rescheduleAt).getTime() <= Date.now()) { toast.error("Pick a time in the future"); return; }
    setBusyId(row.id);
    try {
      const r = await api.post(`/staff/call-logs/${row.id}/snooze`,
                               { next_followup: rescheduleAt, reason: reasonText.trim() });
      toast.success(`Rescheduled → ${fmtDT(r.data.next_followup)}`);
      closeAllEditors();
      load();
    } catch (e) { toast.error(errorMessage(e)); }
    finally { setBusyId(null); }
  };

  const openWhatsApp = (row) => {
    const first = (row.lead_name || "there").split(" ")[0];
    const msg = `Hi ${first}, this is Shakthi — free to talk now?`;
    window.open(`https://wa.me/91${row.customer_number}?text=${encodeURIComponent(msg)}`, "_blank", "noopener,noreferrer");
  };

  const snoozeModeLabel = (m) =>
    m === "30m" ? "30 min" : m === "1h" ? "1 hour" : m === "tomorrow" ? "Tomorrow 10 am" : "";

  return (
    <div className="mb-4 rounded-xl border border-red-500/50 bg-red-500/10 overflow-hidden" data-testid="overdue-followup-banner">
      <button
        type="button"
        onClick={() => setCollapsed(v => !v)}
        data-testid="overdue-toggle"
        className="w-full flex items-center gap-2 px-4 py-3 text-left hover:bg-red-500/10">
        <AlertTriangle className="w-4 h-4 text-red-300 flex-shrink-0"/>
        <div className="flex-1">
          <div className="text-sm font-bold text-red-200">
            {rows.length} overdue follow-up{rows.length > 1 ? "s" : ""}
          </div>
          <div className="text-[11px] text-red-300/80">
            Customers you promised to call back — don't let them slip.
          </div>
        </div>
        <ChevronRight className={`w-4 h-4 text-red-300 transition-transform ${collapsed ? "" : "rotate-90"}`}/>
      </button>

      {!collapsed && (
        <div className="border-t border-red-500/30 divide-y divide-red-500/15 bg-red-500/5">
          {rows.map(r => {
            const isSnoozing = pendingSnooze?.row?.id === r.id;
            const isRescheduling = rescheduleFor === r.id;
            return (
              <div key={r.id} className="px-4 py-2.5" data-testid={`overdue-row-${r.lead_id}`}>
                <div className="flex items-center gap-3">
                  <PhoneCall className="w-3.5 h-3.5 text-red-300 flex-shrink-0"/>
                  <button
                    type="button"
                    onClick={() => onOpenProfile?.({ lead_id: r.lead_id, name: r.lead_name, phone: r.customer_number })}
                    data-testid={`overdue-open-${r.lead_id}`}
                    className="flex-1 min-w-0 text-left">
                    <div className="flex items-center gap-2 text-sm">
                      <span className="font-bold text-white truncate hover:text-amber-300 transition">{r.lead_name}</span>
                      <span className="font-mono-alt text-[10px] text-slate-400">{r.lead_id}</span>
                    </div>
                    <div className="text-[11px] text-red-200/90 mt-0.5">
                      Was <span className="font-mono-alt">{fmtDT(r.next_followup)}</span>
                      {r.remark && <span className="text-slate-400 italic ml-1">· "{r.remark.split("\n")[0]}"</span>}
                    </div>
                  </button>
                  <span className="text-[10px] uppercase tracking-widest bg-red-500/25 text-red-200 border border-red-500/40 rounded-full px-2 py-0.5 whitespace-nowrap">
                    {overdueLabel(r.next_followup)}
                  </span>
                </div>

                {/* Actions strip */}
                <div className="mt-2 flex items-center gap-2 flex-wrap">
                  <button type="button" disabled={busyId === r.id}
                    onClick={() => openSnooze(r, "30m")}
                    data-testid={`snooze-30m-${r.lead_id}`}
                    className={`flex items-center gap-1 text-[11px] px-2.5 py-1 rounded-full border transition disabled:opacity-40 ${isSnoozing && pendingSnooze.mode === "30m" ? "bg-amber-500 border-amber-500 text-slate-950 font-bold" : "border-amber-500/40 bg-amber-500/10 text-amber-200 hover:bg-amber-500/20"}`}>
                    <Clock className="w-3 h-3"/> Snooze 30m
                  </button>
                  <button type="button" disabled={busyId === r.id}
                    onClick={() => openSnooze(r, "1h")}
                    data-testid={`snooze-1h-${r.lead_id}`}
                    className={`flex items-center gap-1 text-[11px] px-2.5 py-1 rounded-full border transition disabled:opacity-40 ${isSnoozing && pendingSnooze.mode === "1h" ? "bg-amber-500 border-amber-500 text-slate-950 font-bold" : "border-amber-500/40 bg-amber-500/10 text-amber-200 hover:bg-amber-500/20"}`}>
                    <Clock className="w-3 h-3"/> 1h
                  </button>
                  <button type="button" disabled={busyId === r.id}
                    onClick={() => openSnooze(r, "tomorrow")}
                    data-testid={`snooze-tomorrow-${r.lead_id}`}
                    className={`flex items-center gap-1 text-[11px] px-2.5 py-1 rounded-full border transition disabled:opacity-40 ${isSnoozing && pendingSnooze.mode === "tomorrow" ? "bg-amber-500 border-amber-500 text-slate-950 font-bold" : "border-amber-500/40 bg-amber-500/10 text-amber-200 hover:bg-amber-500/20"}`}>
                    <Sunrise className="w-3 h-3"/> Tomorrow 10 am
                  </button>
                  <button type="button" disabled={busyId === r.id}
                    onClick={() => openReschedule(r)}
                    data-testid={`reschedule-${r.lead_id}`}
                    className="flex items-center gap-1 text-[11px] px-2.5 py-1 rounded-full border border-blue-500/40 bg-blue-500/10 text-blue-200 hover:bg-blue-500/20 transition disabled:opacity-40">
                    <CalendarClock className="w-3 h-3"/> Reschedule
                  </button>
                  <button type="button"
                    onClick={() => openWhatsApp(r)}
                    data-testid={`wa-${r.lead_id}`}
                    className="flex items-center gap-1 text-[11px] px-2.5 py-1 rounded-full border border-emerald-500/40 bg-emerald-500/10 text-emerald-200 hover:bg-emerald-500/20 transition">
                    <MessageSquare className="w-3 h-3"/> WhatsApp
                  </button>
                </div>

                {/* Inline reason prompt when a snooze preset is selected */}
                {isSnoozing && (
                  <div className="mt-2 rounded-lg bg-slate-900/70 border border-amber-500/30 p-2.5" data-testid={`snooze-editor-${r.lead_id}`}>
                    <div className="text-[10px] uppercase tracking-widest text-amber-300 font-bold mb-1.5">
                      Reason for snoozing · {snoozeModeLabel(pendingSnooze.mode)}
                    </div>
                    <div className="flex flex-wrap gap-1.5 mb-2">
                      {REASON_CHIPS.map(c => (
                        <button key={c} type="button"
                          onClick={() => setReasonText(c)}
                          data-testid={`reason-chip-${c.replace(/\s+/g, "-").toLowerCase()}`}
                          className={`text-[10px] px-2 py-0.5 rounded-full border transition ${reasonText === c ? "bg-slate-700 border-slate-500 text-white" : "border-slate-700 bg-slate-950 text-slate-300 hover:border-slate-500"}`}>
                          {c}
                        </button>
                      ))}
                    </div>
                    <input
                      type="text"
                      value={reasonText}
                      onChange={(e) => setReasonText(e.target.value)}
                      placeholder="Or type a short reason…"
                      data-testid={`reason-input-${r.lead_id}`}
                      className="w-full bg-slate-950 border border-slate-700 text-white text-xs rounded px-2 py-1.5 focus:border-amber-500 focus:outline-none"/>
                    <div className="mt-2 flex items-center gap-2">
                      <button type="button"
                        disabled={busyId === r.id}
                        onClick={confirmSnooze}
                        data-testid={`snooze-confirm-${r.lead_id}`}
                        className="flex items-center gap-1 text-[11px] px-3 py-1 rounded-full bg-amber-500 text-slate-950 font-bold hover:bg-amber-400 disabled:opacity-40">
                        <Check className="w-3 h-3"/> Confirm & Snooze
                      </button>
                      <button type="button"
                        onClick={closeAllEditors}
                        data-testid={`snooze-cancel-${r.lead_id}`}
                        className="flex items-center gap-1 text-[11px] px-2 py-1 rounded-full text-slate-400 hover:text-white">
                        Cancel
                      </button>
                      <span className="ml-auto text-[10px] text-slate-500">Reason is optional</span>
                    </div>
                  </div>
                )}

                {/* Inline reschedule editor */}
                {isRescheduling && (
                  <div className="mt-2 rounded-lg bg-slate-900/70 border border-blue-500/30 p-2.5 space-y-2" data-testid={`reschedule-editor-${r.lead_id}`}>
                    <div className="flex items-center gap-2">
                      <input type="datetime-local" value={rescheduleAt}
                        onChange={(e) => setRescheduleAt(e.target.value)}
                        data-testid={`reschedule-input-${r.lead_id}`}
                        className="flex-1 bg-slate-950 border border-slate-700 text-white text-xs rounded px-2 py-1"/>
                    </div>
                    <input type="text" value={reasonText}
                      onChange={(e) => setReasonText(e.target.value)}
                      placeholder="Reason (optional)…"
                      className="w-full bg-slate-950 border border-slate-700 text-white text-xs rounded px-2 py-1.5"/>
                    <div className="flex items-center gap-2">
                      <button type="button" disabled={busyId === r.id || !rescheduleAt}
                        onClick={() => saveReschedule(r)}
                        data-testid={`reschedule-save-${r.lead_id}`}
                        className="flex items-center gap-1 text-[11px] px-3 py-1 rounded-full bg-blue-500 text-slate-950 font-bold hover:bg-blue-400 disabled:opacity-40">
                        <Check className="w-3 h-3"/> Save
                      </button>
                      <button type="button" onClick={closeAllEditors}
                        data-testid={`reschedule-cancel-${r.lead_id}`}
                        className="flex items-center gap-1 text-[11px] px-2 py-1 rounded-full text-slate-400 hover:text-white">
                        <X className="w-3 h-3"/> Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
